/**
 * Tabs Component - Theme-aware tabbed interface
 *
 * Supports an opt-in "primary + More" overflow pattern (UI-NAV-01..04):
 * pass `overflowKeys` to collapse long-tail tabs into a dropdown.
 *
 * API compatible with Ant Design Tabs for easy migration.
 */

const React = (globalThis as any).React;
const { useState, useEffect, useRef, useCallback } = React;

import { styles } from '../styles';

type ReactNode = any;
type CSSProperties = any;

export interface TabPaneProps {
  tab: React.ReactNode;
  key: string;
  children?: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export interface TabsProps {
  items?: Array<{ key: string; label: React.ReactNode; children?: React.ReactNode; icon?: React.ReactNode; disabled?: boolean }>;
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (activeKey: string) => void;
  type?: 'line' | 'card' | 'editable-card';
  size?: 'small' | 'default' | 'large';
  children?: React.ReactNode;
  style?: React.CSSProperties;
  /** Keys that collapse into a "More" dropdown instead of inline tabs. */
  overflowKeys?: string[];
  /** Label shown on the overflow trigger when no overflow tab is active. */
  overflowLabel?: string;
}

export function TabPane({ tab, key, children, disabled, icon }: TabPaneProps) {
  return null;
}

export function Tabs({
  items,
  activeKey: controlledActiveKey,
  defaultActiveKey,
  onChange,
  type = 'line',
  size = 'default',
  children,
  style,
  overflowKeys,
  overflowLabel = 'More',
}: TabsProps) {
  const initialActiveKey: string = controlledActiveKey || defaultActiveKey || (items?.[0]?.key) || '';
  const [internalActiveKey, setInternalActiveKey] = (useState as any)(initialActiveKey);
  const [moreOpen, setMoreOpen] = (useState as any)(false);
  const tabsRef = (useRef as any)(null);
  const moreRef = (useRef as any)(null);

  const activeKey = controlledActiveKey !== undefined ? controlledActiveKey : internalActiveKey;

  // Extract TabPane children if children prop is used
  const tabPanes: TabPaneProps[] = [];
  if (children) {
    React.Children.forEach(children, (child: any) => {
      if (React.isValidElement(child) && (child.type === TabPane || (child.type as any)?.displayName === 'TabPane')) {
        tabPanes.push({
          tab: child.props.tab,
          key: child.props.key || child.key,
          children: child.props.children,
          disabled: child.props.disabled,
          icon: child.props.icon,
        });
      }
    });
  }

  const allTabs = items || tabPanes.map(pane => ({
    key: pane.key,
    label: pane.tab,
    children: pane.children,
    icon: pane.icon,
    disabled: pane.disabled,
  }));

  const overflowSet = overflowKeys ? new Set(overflowKeys) : null;
  const primaryTabs = overflowSet ? allTabs.filter(t => !overflowSet.has(t.key)) : allTabs;
  const overflowTabs = overflowSet ? allTabs.filter(t => overflowSet.has(t.key)) : [];
  const activeOverflowTab = overflowTabs.find(t => t.key === activeKey);

  const handleTabChange = (key: string) => {
    if (controlledActiveKey === undefined) {
      setInternalActiveKey(key);
    }
    onChange?.(key);
  };

  const handleKeyDown = (e: React.KeyboardEvent, key: string) => {
    const enabledTabs = primaryTabs.filter(tab => !tab.disabled);
    const currentIndex = enabledTabs.findIndex(tab => tab.key === key);

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        if (currentIndex < enabledTabs.length - 1) {
          handleTabChange(enabledTabs[currentIndex + 1].key);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (currentIndex > 0) {
          handleTabChange(enabledTabs[currentIndex - 1].key);
        }
        break;
      case 'Home':
        e.preventDefault();
        if (enabledTabs.length > 0) {
          handleTabChange(enabledTabs[0].key);
        }
        break;
      case 'End':
        e.preventDefault();
        if (enabledTabs.length > 0) {
          handleTabChange(enabledTabs[enabledTabs.length - 1].key);
        }
        break;
    }
  };

  const handleMoreKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setMoreOpen(false);
      moreRef.current?.focus();
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = (e: MouseEvent) => {
      const container = tabsRef.current;
      if (container && !container.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [moreOpen]);

  const activeTab = allTabs.find(tab => tab.key === activeKey) || allTabs[0];

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...style,
  };

  const tabListStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'nowrap',
    borderBottom: type === 'line' ? '1px solid var(--border-color)' : 'none',
    marginBottom: '16px',
    gap: type === 'card' ? '4px' : '0',
  };

  const tabButtonStyle = (tab: typeof allTabs[0]): CSSProperties => ({
    padding: size === 'small' ? '8px 16px' : size === 'large' ? '16px 24px' : '12px 24px',
    background: type === 'card' && activeKey === tab.key ? 'var(--card-bg)' : 'transparent',
    border: type === 'card' ? '1px solid var(--border-color)' : 'none',
    borderBottom: type === 'line' && activeKey === tab.key ? '2px solid var(--button-bg)' : '2px solid transparent',
    color: activeKey === tab.key ? 'var(--button-bg)' : 'var(--main-text-secondary)',
    cursor: tab.disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    opacity: tab.disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    ...(type === 'card' && {
      borderRadius: '8px 8px 0 0',
      marginBottom: '-1px',
    }),
  });

  const contentStyle: CSSProperties = {
    flex: 1,
    overflow: 'auto',
  };

  const moreButtonStyle: CSSProperties = {
    ...tabButtonStyle({ key: '__more__', label: '', disabled: false } as any),
    borderBottom: activeOverflowTab
      ? (type === 'line' ? '2px solid var(--button-bg)' : '2px solid transparent')
      : '2px solid transparent',
    color: activeOverflowTab ? 'var(--button-bg)' : 'var(--main-text-secondary)',
    position: 'relative',
  };

  const dropdownStyle: CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    minWidth: 200,
    background: 'var(--card-bg, #0f172a)',
    border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
    borderRadius: 8,
    padding: 4,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const dropdownItemStyle = (tab: typeof allTabs[0]): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: activeKey === tab.key ? 'var(--card-hover-bg, rgba(255,255,255,0.08))' : 'transparent',
    border: 'none',
    borderRadius: 6,
    color: activeKey === tab.key ? 'var(--button-bg, var(--main-text))' : 'var(--main-text-secondary)',
    fontSize: 13,
    fontWeight: activeKey === tab.key ? 600 : 400,
    cursor: tab.disabled ? 'not-allowed' : 'pointer',
    opacity: tab.disabled ? 0.5 : 1,
    width: '100%',
    textAlign: 'left',
    transition: 'background 0.15s ease',
  });

  const renderPrimaryTabs = () =>
    primaryTabs.map((tab) =>
      React.createElement('button', {
        key: tab.key,
        role: 'tab',
        'aria-selected': activeKey === tab.key,
        'aria-controls': `tabpanel-${tab.key}`,
        id: `tab-${tab.key}`,
        'data-testid': `tab-${tab.key}`,
        tabIndex: activeKey === tab.key ? 0 : -1,
        disabled: tab.disabled,
        onClick: () => !tab.disabled && handleTabChange(tab.key),
        onKeyDown: (e: any) => !tab.disabled && handleKeyDown(e, tab.key),
        style: tabButtonStyle(tab),
      },
        tab.icon,
        tab.label
      )
    );

  const renderMoreMenu = () => {
    if (overflowTabs.length === 0) return null;

    const triggerLabel = activeOverflowTab ? activeOverflowTab.label : overflowLabel;
    const triggerIcon = activeOverflowTab ? activeOverflowTab.icon : null;

    return React.createElement('div', {
      key: '__more-wrapper__',
      style: { position: 'relative' as const },
      onKeyDown: handleMoreKeyDown,
    },
      React.createElement('button', {
        ref: moreRef,
        'data-testid': 'tab-more',
        'aria-haspopup': 'menu',
        'aria-expanded': moreOpen,
        onClick: () => setMoreOpen(!moreOpen),
        style: moreButtonStyle,
      },
        triggerIcon,
        triggerLabel,
        React.createElement('span', {
          style: { fontSize: 10, marginLeft: 4, opacity: 0.6 },
          'aria-hidden': true,
        }, moreOpen ? '\u25B2' : '\u25BC')
      ),

      moreOpen && React.createElement('div', {
        role: 'menu',
        'data-testid': 'more-menu-dropdown',
        style: dropdownStyle,
      },
        ...overflowTabs.map(tab =>
          React.createElement('button', {
            key: tab.key,
            role: 'menuitem',
            'data-testid': `tab-${tab.key}`,
            disabled: tab.disabled,
            onClick: () => {
              if (!tab.disabled) {
                handleTabChange(tab.key);
                setMoreOpen(false);
              }
            },
            style: dropdownItemStyle(tab),
          },
            tab.icon && React.createElement('span', { style: { width: 20, textAlign: 'center' } }, tab.icon),
            React.createElement('span', { style: { flex: 1 } }, tab.label),
          )
        )
      )
    );
  };

  return React.createElement('div', { style: containerStyle, ref: tabsRef },
    React.createElement('div', {
      role: 'tablist',
      'aria-label': 'Tabs',
      style: tabListStyle,
    },
      ...renderPrimaryTabs(),
      renderMoreMenu(),
    ),
    activeTab && React.createElement('div', {
      role: 'tabpanel',
      id: `tabpanel-${activeTab.key}`,
      'aria-labelledby': `tab-${activeTab.key}`,
      style: contentStyle,
    }, activeTab.children)
  );
}

(Tabs as any).TabPane = TabPane;
(Tabs as any).Tab = TabPane;

